'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  List,
  X,
  Headphones,
  ChevronUp,
  ChevronDown,
  Trash2,
} from 'lucide-react';
import { useAudio, type QueueTrack } from '@/lib/contexts/ListenQueueContext.shim';
import { useAuth } from '@/lib/hooks/useAuth';
import {
  fetchUserPlaylists,
  createPlaylist,
  addToPlaylist,
  type Playlist,
} from '@/lib/supabase/playlists';
import { cn, formatClock } from '@/lib/utils';
import { getEvidenceTierInfo } from '@/lib/articles';
import type { EvidenceTier } from '@/lib/types';

/**
 * ListenDock — persistent bottom-center audio dock.
 *
 * States:
 *   1. Hidden            — no queue AND scroll < 600px        → render null
 *   1b. Hidden           — dismissed by user (queue preserved) → render null
 *   2. Empty hint pill   — no queue AND scroll ≥ 600px        → "Build a listen queue"
 *   3. Collapsed bar     — queue has items + showPlayerBar    → compact player
 *   4. Expanded panel    — user clicks the queue toggle        → panel above bar
 *
 * Dismiss: the X on the collapsed bar / expanded head pauses audio and hides
 * the dock without clearing the queue. The dock re-appears the next time the
 * user clicks "Listen now" or "Add to queue" on any article (those actions
 * flip `showPlayerBar` back to true).
 *
 * Auth-gated playlist section appears inside the expanded panel for signed-in users.
 * See vault/brand/Aviado Brand Guide v3.0 for layout + color guidance.
 */

const SCROLL_THRESHOLD = 600;

export function ListenDock() {
  const {
    queue,
    queueIndex,
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    playTrack,
    togglePlayPause,
    seek,
    skipNext,
    skipPrev,
    removeFromQueue,
    moveInQueue,
    clearQueue,
    showPlayerBar,
    dismissPlayerBar,
  } = useAudio();

  const [scrolled, setScrolled] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Scroll threshold for empty-state pill
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Dismiss flow: pause + hide the dock and collapse any open panel, so the
  // next time `showPlayerBar` flips back to true the dock doesn't re-open
  // into a stale expanded state.
  const handleDismiss = () => {
    dismissPlayerBar();
    setExpanded(false);
  };

  const hasQueue = queue.length > 0;

  // State 1: nothing to show yet
  if (!hasQueue && !scrolled) return null;

  // State 2: empty pill — shown on scroll whenever the queue is empty.
  // Intentionally ignores showPlayerBar; the pill is a discovery prompt,
  // not part of the playback surface.
  if (!hasQueue) {
    return (
      <div className="fixed left-1/2 bottom-4 z-[80] -translate-x-1/2 pointer-events-none">
        <div
          aria-label="Build a listen queue — add articles to listen back to back"
          className={cn(
            'pointer-events-auto',
            'flex items-center gap-2.5 px-4 py-2.5',
            'rounded-full bg-warm-navy text-white',
            'shadow-lg',
            'font-ui text-[13px] font-medium',
            'opacity-90 hover:opacity-100 hover:-translate-y-0.5',
            'transition-all duration-fast ease-standard'
          )}
        >
          <Headphones className="w-4 h-4 text-aviado-teal" />
          <span>Build a listen queue</span>
          <span className="pl-2.5 ml-1 border-l border-white/20 font-mono text-[10.5px] text-white/[0.55]">
            String articles into a playlist
          </span>
        </div>
      </div>
    );
  }

  // State 1b: queue present but user dismissed the dock — stay hidden until
  // they take another audio action (Listen now / Add to queue flip showPlayerBar).
  if (!showPlayerBar) return null;

  // State 3/4: bar with queue (+ expanded panel above when open)
  return (
    <>
      <ExpandedPanel
        open={expanded}
        onClose={() => setExpanded(false)}
        onDismiss={handleDismiss}
        queue={queue}
        queueIndex={queueIndex}
        isPlaying={isPlaying}
        onPlayTrack={playTrack}
        onRemove={removeFromQueue}
        onMove={moveInQueue}
        onClear={clearQueue}
      />
      <DockBar
        queue={queue}
        queueIndex={queueIndex}
        currentTrack={currentTrack}
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((e) => !e)}
        onTogglePlayPause={togglePlayPause}
        onSeek={seek}
        onSkipNext={skipNext}
        onSkipPrev={skipPrev}
        onDismiss={handleDismiss}
      />
    </>
  );
}

// ============================================================================
// Collapsed / Bar
// ============================================================================

interface DockBarProps {
  queue: QueueTrack[];
  queueIndex: number;
  currentTrack: QueueTrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  onTogglePlayPause: () => void;
  onSeek: (t: number) => void;
  onSkipNext: () => void;
  onSkipPrev: () => void;
  onDismiss: () => void;
}

function DockBar({
  queue,
  queueIndex,
  currentTrack,
  isPlaying,
  currentTime,
  duration,
  expanded,
  onToggleExpanded,
  onTogglePlayPause,
  onSeek,
  onSkipNext,
  onSkipPrev,
  onDismiss,
}: DockBarProps) {
  const now = currentTrack ?? queue[Math.max(0, queueIndex)] ?? queue[0];
  if (!now) return null;

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const label = isPlaying ? `Now playing · ${queueIndex + 1} of ${queue.length}` : `Up next · ${queueIndex + 1} of ${queue.length}`;

  return (
    <div
      role="region"
      aria-label="Listen queue"
      className={cn(
        'fixed left-1/2 bottom-4 z-[80] -translate-x-1/2 pointer-events-none',
        'w-[min(520px,calc(100vw-32px))]'
      )}
    >
      <div
        className={cn(
          'pointer-events-auto',
          'grid grid-cols-[auto_1fr_auto] gap-4 items-center',
          'px-3 py-3',
          'bg-warm-navy text-white',
          'rounded-lg',
          'shadow-lg'
        )}
        style={{ borderRadius: expanded ? '0 0 14px 14px' : '14px' }}
      >
        {/* Play / Pause */}
        <button
          onClick={onTogglePlayPause}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          className={cn(
            'w-10 h-10 rounded-full bg-aviado-teal text-warm-navy',
            'inline-flex items-center justify-center shrink-0',
            'transition-transform duration-fast hover:scale-105'
          )}
        >
          {isPlaying ? <Pause className="w-[18px] h-[18px]" /> : <Play className="w-[18px] h-[18px] ml-[1px]" />}
        </button>

        {/* Now-playing */}
        <div className="min-w-0">
          <div className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-aviado-teal mb-[3px]">{label}</div>
          <div className="font-heading text-[13.5px] text-white truncate mb-1.5">{now.title}</div>
          <button
            type="button"
            onClick={(e) => {
              const target = e.currentTarget as HTMLButtonElement;
              const rect = target.getBoundingClientRect();
              const p = (e.clientX - rect.left) / rect.width;
              onSeek(p * duration);
            }}
            aria-label="Seek"
            className="block w-full h-[3px] rounded-sm bg-white/10 overflow-hidden"
          >
            <div className="h-full bg-aviado-teal transition-[width] duration-100" style={{ width: `${pct}%` }} />
          </button>
          <div className="flex justify-between font-mono text-[10px] text-white/[0.55] mt-1">
            <span>{formatClock(currentTime)}</span>
            <span>{formatClock(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1">
          <DockIconButton onClick={onSkipPrev} aria-label="Previous track">
            <SkipBack className="w-[14px] h-[14px]" />
          </DockIconButton>
          <DockIconButton onClick={onSkipNext} aria-label="Next track">
            <SkipForward className="w-[14px] h-[14px]" />
          </DockIconButton>
          <button
            onClick={onToggleExpanded}
            aria-label={expanded ? 'Collapse queue' : 'Expand queue'}
            aria-expanded={expanded}
            className={cn(
              'flex items-center gap-1 px-2.5 py-[7px] rounded-md',
              'bg-white/[0.08] text-white hover:bg-white/15',
              'font-mono text-[11px]',
              'transition-colors duration-fast'
            )}
          >
            <List className="w-[13px] h-[13px]" />
            <span className="font-medium">{queue.length}</span>
          </button>
          <DockIconButton onClick={onDismiss} aria-label="Dismiss player">
            <X className="w-[14px] h-[14px]" />
          </DockIconButton>
        </div>
      </div>
    </div>
  );
}

function DockIconButton({
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      className={cn(
        'w-8 h-8 rounded-md',
        'bg-transparent text-white/70 hover:bg-white/10 hover:text-white',
        'inline-flex items-center justify-center',
        'transition-colors duration-fast',
        'disabled:text-white/20'
      )}
    >
      {children}
    </button>
  );
}

// ============================================================================
// Expanded Panel (queue + auth-gated playlists)
// ============================================================================

interface ExpandedPanelProps {
  open: boolean;
  onClose: () => void;
  onDismiss: () => void;
  queue: QueueTrack[];
  queueIndex: number;
  isPlaying: boolean;
  onPlayTrack: (t: QueueTrack) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, delta: -1 | 1) => void;
  onClear: () => void;
}

function ExpandedPanel({
  open,
  onClose,
  onDismiss,
  queue,
  queueIndex,
  isPlaying,
  onPlayTrack,
  onRemove,
  onMove,
  onClear,
}: ExpandedPanelProps) {
  const { isAuthenticated, loading: authLoading, signInUrl } = useAuth();
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [saving, setSaving] = useState(false);
  const totalMin = Math.ceil(queue.reduce((s, t) => s + (t.durationSeconds || 0), 0) / 60);

  useEffect(() => {
    if (isAuthenticated && open) {
      fetchUserPlaylists().then(setPlaylists).catch(() => {});
    }
  }, [isAuthenticated, open]);

  const onSaveQueue = async () => {
    if (!isAuthenticated || queue.length === 0) return;
    setSaving(true);
    try {
      const name = `Queue — ${new Date().toLocaleDateString()}`;
      const pl = await createPlaylist(name);
      if (!pl) {
        // Silent failure — createPlaylist logs its own error. Consider a toast
        // here once the shared toast util lands (Phase 3/7).
        return;
      }
      // Parallel inserts so a 20-track save is ~1 RTT, not 20.
      await Promise.all(queue.map((track, i) => addToPlaylist(pl.id, track.id, i)));
      setPlaylists((prev) => [pl, ...prev]);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop for outside-click close on mobile */}
      <button
        type="button"
        aria-label="Close queue panel"
        onClick={onClose}
        className="fixed inset-0 z-[70] bg-black/0 cursor-default"
      />
      <div
        role="region"
        aria-label="Queue"
        className={cn(
          'fixed left-1/2 bottom-[76px] z-[80] -translate-x-1/2 pointer-events-auto',
          'w-[min(520px,calc(100vw-32px))]',
          'bg-warm-navy text-white',
          'rounded-t-lg shadow-lg',
          'overflow-hidden',
          'animate-slide-down'
        )}
      >
        {/* Head */}
        <div className="flex justify-between items-start px-5 pt-[18px] pb-3 border-b border-white/[0.08]">
          <div>
            <h5 className="font-display font-medium text-[18px] text-white leading-tight mb-1">
              Your listen queue
            </h5>
            <div className="font-mono text-[10.5px] tracking-[0.04em] text-white/[0.55]">
              {queue.length} articles {totalMin > 0 && `· ${totalMin} min total`} · saved locally
            </div>
          </div>
          <div className="flex items-start gap-2">
            <button
              onClick={onClear}
              className="px-2.5 py-1.5 rounded-md border border-white/15 text-white/70 hover:text-white hover:border-white/30 font-ui text-[11.5px] transition-colors duration-fast"
            >
              Clear all
            </button>
            <button
              onClick={onDismiss}
              aria-label="Dismiss player"
              className="w-7 h-7 rounded-md bg-transparent text-white/60 hover:bg-white/[0.08] hover:text-white inline-flex items-center justify-center transition-colors duration-fast"
            >
              <X className="w-[14px] h-[14px]" />
            </button>
          </div>
        </div>

        {/* List */}
        <ol className="list-none m-0 py-1 max-h-[340px] overflow-y-auto">
          {queue.map((track, i) => (
            <QueueItem
              key={track.id}
              track={track}
              position={i}
              isCurrent={i === queueIndex}
              isPlaying={isPlaying}
              onPlay={() => onPlayTrack(track)}
              onRemove={() => onRemove(track.id)}
              onMoveUp={() => onMove(track.id, -1)}
              onMoveDown={() => onMove(track.id, +1)}
            />
          ))}
        </ol>

        {/* Auth-gated playlist section — suppressed during initial session
            resolution so users don't see a "Sign in" flash before their
            session loads. */}
        {authLoading ? null : isAuthenticated ? (
          <div className="px-5 py-3 border-t border-white/[0.08]">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-aviado-teal">
                Your playlists
              </div>
              <button
                onClick={onSaveQueue}
                disabled={saving || queue.length === 0}
                className="font-ui text-[11.5px] text-aviado-teal hover:text-white disabled:text-white/30 transition-colors duration-fast"
              >
                {saving ? 'Saving…' : 'Save queue as playlist'}
              </button>
            </div>
            {playlists.length > 0 ? (
              <ul className="list-none m-0 p-0 space-y-1">
                {playlists.slice(0, 4).map((p) => (
                  <li key={p.id} className="font-ui text-[12.5px] text-white/80 truncate">
                    • {p.name}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="font-ui text-[12px] text-white/50">No saved playlists yet.</div>
            )}
          </div>
        ) : queue.length >= 2 ? (
          <div className="px-5 py-3 border-t border-white/[0.08] flex items-center justify-between">
            <div className="font-ui text-[12px] text-white/[0.65] leading-snug">
              Sign in to save your queue and sync across devices.
            </div>
            <a
              href={signInUrl}
              className="ml-3 shrink-0 px-3 py-1.5 rounded-md bg-aviado-teal text-warm-navy font-ui text-[11.5px] font-medium hover:bg-white transition-colors duration-fast"
            >
              Sign in
            </a>
          </div>
        ) : null}

        {/* Foot */}
        <div className="flex justify-between px-5 py-3 border-t border-white/[0.08] font-mono text-[10.5px] tracking-[0.02em] text-white/[0.45]">
          <span>↑↓ reorder · tap to play · queue persists across pages</span>
          <Link href="/browse" className="text-aviado-teal hover:text-white">
            Browse library →
          </Link>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// Queue item
// ============================================================================

interface QueueItemProps {
  track: QueueTrack;
  position: number;
  isCurrent: boolean;
  isPlaying: boolean;
  onPlay: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function QueueItem({
  track,
  position,
  isCurrent,
  isPlaying,
  onPlay,
  onRemove,
  onMoveUp,
  onMoveDown,
}: QueueItemProps) {
  const tierInfo = track.evidenceTier
    ? getEvidenceTierInfo(track.evidenceTier as EvidenceTier)
    : null;

  return (
    <li
      className={cn(
        'grid grid-cols-[auto_1fr_auto] gap-3 items-center',
        'px-5 py-2.5',
        'border-b border-white/[0.04]',
        'transition-colors duration-fast',
        isCurrent ? 'bg-aviado-teal/10' : 'hover:bg-white/[0.04]'
      )}
    >
      <button
        onClick={onPlay}
        aria-label={`Play ${track.title}`}
        className="flex items-center gap-2.5 bg-transparent border-0 text-white cursor-pointer px-2 py-1 rounded-md hover:bg-white/[0.08] transition-colors duration-fast"
      >
        <span
          className={cn(
            'font-mono text-[10.5px] tracking-[0.04em] w-[18px] text-right',
            isCurrent ? 'text-aviado-teal' : 'text-white/40'
          )}
        >
          {isCurrent ? (isPlaying ? <Pause className="w-[11px] h-[11px] inline" /> : <Play className="w-[11px] h-[11px] inline" />) : String(position + 1).padStart(2, '0')}
        </span>
      </button>

      <div className="min-w-0">
        <div
          className={cn(
            'font-heading font-medium text-[13.5px] leading-snug mb-1 truncate',
            isCurrent ? 'text-white' : 'text-white/80'
          )}
        >
          {track.title}
        </div>
        <div className="flex gap-2 items-center font-mono text-[10px] tracking-[0.02em] text-white/50">
          {tierInfo && (
            <span className={cn(tierInfo.bgClass, 'text-[9px] !px-1.5 !py-[1px]')}>{tierInfo.label}</span>
          )}
          <span className="truncate">{track.supplement}</span>
        </div>
      </div>

      <div className="flex gap-[2px]">
        <IconSmall onClick={onMoveUp} aria-label="Move up">
          <ChevronUp className="w-[13px] h-[13px]" />
        </IconSmall>
        <IconSmall onClick={onMoveDown} aria-label="Move down">
          <ChevronDown className="w-[13px] h-[13px]" />
        </IconSmall>
        <IconSmall onClick={onRemove} aria-label="Remove">
          <X className="w-[13px] h-[13px]" />
        </IconSmall>
      </div>
    </li>
  );
}

function IconSmall({ children, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      className="w-6 h-6 rounded-[4px] bg-transparent border-0 text-white/40 hover:bg-white/[0.08] hover:text-white inline-flex items-center justify-center transition-colors duration-fast"
    >
      {children}
    </button>
  );
}

export default ListenDock;
